/**
 * Token-attaching `fetch` wrapper for the dashboard SPA (Concern 1 — Entra SSO).
 *
 * `createAuthedFetch` returns a function with the exact `typeof fetch` shape so
 * it drops into the existing injectable `fetchFn` slots (`useRunLoader`,
 * `RepoDetail`, `fetchRunsCatalog`). It acquires an Entra access token from the
 * injected {@link TokenClient}, attaches it as `Authorization: Bearer <token>`,
 * and implements the edge-rejection recovery semantics:
 *
 *  - On HTTP 401: one silent `refreshToken()` + exactly one retry; if the retry
 *    is still 401, `onUnauthenticated()` is invoked and the 401 is returned
 *    (Req 4.1, 4.2, 4.3).
 *  - On HTTP 403: `onAccessDenied()` is invoked and the response returned with
 *    no retry (Req 5.3).
 *  - Otherwise the response is returned unchanged.
 *
 * The retry budget is at most one per original request. Caller-supplied `input`
 * and `init` are never mutated — headers are merged into fresh objects and
 * `Request` inputs are cloned per attempt. Tokens are never logged (Req 18.2).
 *
 * The module depends only on the injected {@link TokenClient}, not on
 * `@viamericas/auth-react`, so it builds without the restricted package.
 *
 * Requirements: 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 5.3, 18.2.
 */

/**
 * Minimal token interface the wrapper depends on. The concrete implementation
 * is provided by a thin adapter over `@viamericas/auth-react` (task 3.1), which
 * keeps this module free of the restricted package.
 */
export interface TokenClient {
  /** Acquire a token silently (MSAL reuses a valid cached token, Req 3.5/3.6). */
  getToken(): Promise<string>;
  /** Force a silent refresh, bypassing the cached token (Req 4.1). */
  refreshToken(): Promise<string>;
  /** Begin interactive sign-in (redirect/popup). */
  signIn(): Promise<void>;
}

/**
 * Navigation callbacks invoked on terminal authorization outcomes. These route
 * the user to the sign-in or access-denied screens; they perform no fetching.
 */
export interface AuthedFetchHandlers {
  /** Called when, after one refresh+retry, a 401 persists (Req 4.2). */
  onUnauthenticated: () => void;
  /** Called on a 403 from the edge (Req 5.3). */
  onAccessDenied: () => void;
}

/** HTTP statuses that drive the wrapper's recovery behavior. */
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;

/**
 * Collect the caller's headers (from a `Request` input and/or `init`) into a
 * fresh `Headers` instance without mutating either source. When both carry a
 * header, `init` wins, matching `fetch(request, init)` semantics.
 */
function mergeBaseHeaders(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Headers {
  const headers = new Headers();
  if (input instanceof Request) {
    input.headers.forEach((value, key) => headers.set(key, value));
  }
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  return headers;
}

/**
 * Build a fresh `RequestInit` for one attempt: a copy of the caller's `init`
 * with the merged headers plus an `Authorization: Bearer <token>` header. The
 * caller's `init` and the shared `base` headers are left untouched.
 */
function buildAttemptInit(
  base: Headers,
  init: RequestInit | undefined,
  token: string,
): RequestInit {
  const headers = new Headers(base);
  headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

/**
 * Produce the `input` argument for one attempt. `Request` inputs are cloned so
 * a retry does not reuse an already-consumed body/stream; strings and URLs are
 * safe to pass through unchanged.
 */
function prepareInput(input: RequestInfo | URL): RequestInfo | URL {
  return input instanceof Request ? input.clone() : input;
}

/**
 * Create a token-attaching `fetch` wrapper.
 *
 * @param tokens    Token client used to acquire/refresh access tokens.
 * @param handlers  Navigation callbacks for terminal 401/403 outcomes.
 * @param baseFetch Underlying fetch implementation (defaults to global `fetch`).
 * @returns A function matching `typeof fetch`.
 */
export function createAuthedFetch(
  tokens: TokenClient,
  handlers: AuthedFetchHandlers,
  baseFetch: typeof fetch = fetch,
): typeof fetch {
  const authedFetch: typeof fetch = async (input, init) => {
    const base = mergeBaseHeaders(input, init);

    // Attempt 1: attach the current (silently acquired) token.
    const token = await tokens.getToken();
    const first = await baseFetch(
      prepareInput(input),
      buildAttemptInit(base, init, token),
    );

    // 401 → exactly one silent refresh + one retry (Req 4.1, 4.3).
    if (first.status === STATUS_UNAUTHORIZED) {
      const refreshed = await tokens.refreshToken();
      const retried = await baseFetch(
        prepareInput(input),
        buildAttemptInit(base, init, refreshed),
      );

      if (retried.status === STATUS_UNAUTHORIZED) {
        // Persistent 401 → back to sign-in (Req 4.2).
        handlers.onUnauthenticated();
      } else if (retried.status === STATUS_FORBIDDEN) {
        // A role failure surfaced on retry → access-denied (Req 5.3).
        handlers.onAccessDenied();
      }
      return retried;
    }

    // 403 → access-denied, no retry (Req 5.3).
    if (first.status === STATUS_FORBIDDEN) {
      handlers.onAccessDenied();
      return first;
    }

    // Any other outcome is returned unchanged for the caller's normal handling.
    return first;
  };

  return authedFetch;
}
